// useQuizKeyboard — keyboard shortcuts for MCQ/TF answer sessions.
//
// Extracted from mock-exam-view.tsx and quiz-view.tsx which had ~60 lines of
// near-identical keydown handler logic (A-D/1-4 for MCQ, T/F/1/2 for TF,
// navigation keys, bookmark/mark shortcuts).
//
// The hook handles option picking + navigation key mapping.  Per-view guards
// (e.g. "don't advance until answered" in quiz) are expressed as closures
// passed via config callbacks.
//
// Session 14 Step 4 · Tier 2 View Decomposition.

import { useEffect } from 'react';
import { isTypingTarget } from '../keyboard.js';
import { keyToOptionIndex } from '../quiz-utils.js';

export function useQuizKeyboard(
  isActive: boolean,
  config: {
    kind: 'mcq' | 'tf' | 'essay' | 'free_response' | 'proof_drill';
    onPickOption: (idx: number) => void;
    /** ArrowRight — navigate to next item (mock-exam always, quiz after answer). */
    onNext?: () => void;
    /** ArrowLeft — navigate to previous item. */
    onPrev?: () => void;
    /** Enter / Space — submit-then-advance (quiz only, after picking). */
    onSubmit?: () => void;
    /** B — bookmark toggle (quiz only). */
    onBookmark?: () => void;
    /** M — mark for review (mock-exam only). */
    onToggleMarked?: () => void;
    /** MCQ only: option count for bounds check. Omit to skip check (mock-exam). */
    mcqOptionCount?: number;
  },
): void {
  useEffect(() => {
    if (!isActive) return;

    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;

      // ── Option picking ────────────────────────────────────────────────
      if (config.kind !== 'tf') {
        const idx = keyToOptionIndex(e.key);
        if (idx !== null) {
          if (config.mcqOptionCount === undefined || idx < config.mcqOptionCount) {
            e.preventDefault();
            config.onPickOption(idx);
            return; // don't also trigger navigation on the same key
          }
        }
      } else {
        const lower = e.key.toLowerCase();
        if (lower === 't' || lower === '1' || lower === 'y') {
          e.preventDefault();
          config.onPickOption(0);
          return;
        } else if (lower === 'f' || lower === '2' || lower === 'n') {
          e.preventDefault();
          config.onPickOption(1);
          return;
        }
      }

      // ── Navigation / actions ──────────────────────────────────────────
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        config.onNext?.();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        config.onPrev?.();
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        config.onSubmit?.();
      }
      if (e.key.toLowerCase() === 'b') {
        e.preventDefault();
        config.onBookmark?.();
      }
      if (e.key.toLowerCase() === 'm') {
        e.preventDefault();
        config.onToggleMarked?.();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, config]);
}
